'use client'

import React, { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react'
import { features } from '@/lib/config'

const HEADER_ROOT_ID = 'header-root'
const MARKETPLACE_CART_ATTRIBUTE = 'data-marketplace-cart-enabled'

type HeaderFeatures = {
  marketplaceCartEnabled: boolean | null
}

const HeaderFeaturesContext = createContext<HeaderFeatures | undefined>(undefined)

const readMarketplaceCartEnabled = (headerRoot: HTMLElement): boolean | null => {
  const value = headerRoot.getAttribute(MARKETPLACE_CART_ATTRIBUTE)

  if (value === '1') return true
  if (value === '0') return false

  return null
}

export function HeaderFeaturesProvider({ children }: PropsWithChildren) {
  const [marketplaceCartEnabled, setMarketplaceCartEnabled] = useState<boolean | null>(
    features.marketplaceCart ? null : false
  )

  useEffect(() => {
    if (!features.marketplaceCart) return
    let headerAttributeObserver: MutationObserver | null = null

    const observeHeaderRoot = () => {
      const headerRoot = document.getElementById(HEADER_ROOT_ID)

      if (!headerRoot) {
        return false
      }

      const syncMarketplaceCartEnabled = () => {
        setMarketplaceCartEnabled(readMarketplaceCartEnabled(headerRoot))
      }

      syncMarketplaceCartEnabled()

      headerAttributeObserver = new MutationObserver(syncMarketplaceCartEnabled)
      headerAttributeObserver.observe(headerRoot, {
        attributes: true,
        attributeFilter: [MARKETPLACE_CART_ATTRIBUTE],
      })

      return true
    }

    const headerRootObserver = new MutationObserver(() => {
      if (observeHeaderRoot()) {
        headerRootObserver.disconnect()
      }
    })

    if (!observeHeaderRoot()) {
      headerRootObserver.observe(document.body, { childList: true, subtree: true })
    }

    return () => {
      headerRootObserver.disconnect()
      headerAttributeObserver?.disconnect()
    }
  }, [])

  return <HeaderFeaturesContext.Provider value={{ marketplaceCartEnabled }}>{children}</HeaderFeaturesContext.Provider>
}

export const useHeaderFeatures = () => {
  const context = useContext(HeaderFeaturesContext)

  if (context === undefined) {
    throw new Error('useHeaderFeatures must be used within a HeaderFeaturesProvider')
  }

  return context
}
